import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Types
import QuizTypes from 'types/QuizTypes';
import QuestionTypes from 'types/QuestionTypes';
// Models
import IQuiz from 'models/Quiz';
import IQuestion from 'models/Question';
import IAnswer, { IAnswerResult } from 'models/Answer';
import IBucket, { IBucketResult } from 'models/Bucket';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Actions
import { setAnswerTime, setAnswers, setBuckets, setQuestionAnswerRefs, setQuestionEnd } from 'store/player/playerSlice';
// Selectors
import { selectQuiz, selectQuestion } from 'store/quiz/quizSelectors';
import {
  selectQuestionReady, selectQuestionAnswerRefs
} from 'store/player/playerSelectors';
// 
import AnswerButton from './AnswerButton';

const PersonalityAnswers:FC = () => {
  const { t } = useTranslation('common');
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const quiz:IQuiz = useAppSelector(selectQuiz);
  const question:IQuestion = useAppSelector(selectQuestion);
  const questionReady:boolean = useAppSelector(selectQuestionReady);
  const questionAnswerRefs:string[] = useAppSelector(selectQuestionAnswerRefs);

  const interval = useRef<any>(null);
  const [ time, setTime ] = useState<number>(0);

  const handleClick = (answerRef:string) => {
    if ( question.type === QuestionTypes.SingleChoice ){
      const foundedAnswer = question.answers.find((answer:IAnswer) => answer.ref === answerRef);
      if ( !foundedAnswer ) return;
      const answer:IAnswerResult = {
        ref: foundedAnswer.ref,
        title: foundedAnswer.title,
        time,
        question: {
          ref: question.ref,
          title: question.title
        }
      };
      const buckets = quiz.buckets.reduce((acc:{ [key:string]:IBucketResult }, cur:IBucket) => {
        const foundedAnswerBucket = foundedAnswer.bucketScores.find((b:any) => b.ref === cur.ref);
        const score = !foundedAnswerBucket ? 0 : foundedAnswerBucket.score;
        acc[cur.ref] = {...cur, score};
        return acc;
      }, {});
      dispatch(setAnswers([answer]));
      dispatch(setBuckets(buckets));
      dispatch(setQuestionEnd(true));
      dispatch(setQuestionAnswerRefs([answerRef]));
    } else {
      const filteredRefs = questionAnswerRefs.includes(answerRef)
        ? questionAnswerRefs.filter((ref:string) => ref !== answerRef)
        : [...questionAnswerRefs, answerRef]
      ;
      dispatch(setQuestionAnswerRefs(filteredRefs));
      dispatch(setAnswerTime(time));
    }
  }

  const handleClearInterval = () => {
    if ( interval.current ) clearInterval(interval.current);
  }

  useEffect(() => {
    const start = new Date().getTime();

    interval.current = setInterval(() => {
      const end = new Date().getTime();
      const time = Math.ceil((end - start)/10);
      setTime(time / 1000);
    });

    return () => {
      handleClearInterval();
    }
  }, []);

  if ( [QuizTypes.Scoring, QuizTypes.Prediction].includes(quiz.type) ) return null;
  return (
    <div className="py-2 px-4">
      <div className="flex flex-col gap-4 mb-4">
        {question.answers.map((answer:IAnswer) => (
          <AnswerButton
            key={`answer-item-${answer.ref}`}
            answer={answer}
            onClick={handleClick}
            disabled={!questionReady}
          />
        ))}
      </div>
      {!questionReady && (
        <span className="block text-gray-500 text-md font-bold text-center">
          {question.video ? t('quiz.questionPage.readyToAnswerVideo') : t('quiz.questionPage.readyToAnswerImage')}
        </span>
      )}
    </div>
  )
}

export default PersonalityAnswers;
